import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { VariableDetails } from '../../../../shared/ontology/model/variable-details';
import { VariableTypeEnum } from '../../../../shared/ontology/variable-type.enum';
import { VariableService } from '../../../../shared/ontology/service/variable.service';
import { VariableUtilityService } from '../../../../study-editor/shared/services/variable-utility.service';
import { VariableFilterRequest } from '../../../../shared/ontology/model/variable-filter-request';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'jhi-input-variable',
    templateUrl: './input-variable.component.html',
    styleUrls: ['./input-variable.component.scss',]

})
export class InputVariableComponent implements OnInit {
    protected readonly Number = Number;

    @Output() variableSelect = new EventEmitter<VariableDetails>();
    // Filters
    @Input() variableTypeIds: VariableTypeEnum[] = [];
    @Input() excludedVariableIds: number[] = [];
    @Input() excludedVariableNames: string[] = [];
    @Input() disableVariablesFromSelector: number[] = [];
    @Input() selectionType: 'single' | 'multiple' = 'multiple';
    @Input() value: any;
    @Input() multiple = false;

    @Output() onVariableSelectedChange: EventEmitter<{ [key: string]: VariableDetails }> = new EventEmitter<{ [key: string]: VariableDetails }>();

    addedVariableIds: string[] = []; // To keep track of added variables
    variables: VariableDetails[] = [];
    variableById: { [key: string]: VariableDetails } = {};
    groupedVariables: { [key: string]: VariableDetails[] } = {};
    searchText: string = '';
    filteredGroupedVariables: { [key: string]: VariableDetails[] } = {};
    isLoading = true;

    constructor(private variableService: VariableService,
                private variableUtilityService: VariableUtilityService) {
    }

    ngOnInit(): void {
        this.disableVariablesFromSelector = this.disableVariablesFromSelector || [];
        const filterRequest = <VariableFilterRequest>({});
        if (this.variableTypeIds && this.variableTypeIds.length) {
            filterRequest.variableTypeIds = this.variableTypeIds.map((enumType) => enumType.toString());
        }
        this.variableService.filterVariables(filterRequest).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe((variables) => {
            this.variables = this.transform(variables);
            this.filterVariables();
        });
    }

    transform(variables: VariableDetails[]): any[] {
        const filteredVariables = variables.filter((variable) => (
            (!this.excludedVariableIds || !this.excludedVariableIds.includes(parseInt(variable.id, 10)))
            && (!this.excludedVariableNames || !this.excludedVariableNames.includes(variable.name.toUpperCase()))));

        this.variableById = variables.map(variable => ({
            [variable.id]: variable
        })).reduce((acc, cur) => ({
            ...acc,
            ...cur
        }), {});

        // Group variables by property.id
        this.groupedVariables = filteredVariables.reduce((groups, variable) => {
            const propertyId = variable.property?.id || 'undefined';
            if (!groups[propertyId]) {
                groups[propertyId] = [];
            }
            groups[propertyId].push(variable);
            return groups;
        }, {} as { [key: string]: VariableDetails[] });

        return filteredVariables;
    }

    filterVariables() {
        let filtered = { ...this.groupedVariables };
        // Then apply text search if exists
        if (this.searchText) {
            const searchTerm = this.searchText.toLowerCase();
            filtered = Object.keys(filtered).reduce((acc, key) => {
                const filteredByText = filtered[key].filter(variable =>
                    variable.name?.toLowerCase().includes(searchTerm) ||
                    variable.description?.toLowerCase().includes(searchTerm) ||
                    variable.property?.name?.toLowerCase().includes(searchTerm) ||
                    variable.property?.classes?.toString().toLowerCase().includes(searchTerm)
                );

                if (filteredByText.length > 0) {
                    acc[key] = filteredByText;
                }
                return acc;
            }, {} as { [key: string]: VariableDetails[] });
        }

        // Exclude added variables
        Object.keys(filtered).forEach((key) => {
            filtered[key] = filtered[key].filter((variable) => !this.addedVariableIds.includes(variable.id));
            if (filtered[key].length === 0) {
                delete filtered[key]; // Remove empty groups
            }
        });
        this.filteredGroupedVariables = filtered;
    }

    highlightText(text: string): string {
        if (!text && !this.searchText) {
            return text;
        }
        const searchTerm = this.searchText.toLowerCase();
        const index = text.toLowerCase().indexOf(searchTerm);
        if (index === -1) {
            return text;
        }
        return text.slice(0, index) +
            `<span class="text-highlight">${text.slice(index, index + this.searchText.length)}</span>` +
            text.slice(index + this.searchText.length);
    }

    onAddVariable(variable: VariableDetails) {

        if (this.selectionType === 'single') {
            this.addedVariableIds = [variable.id];
            this.variableSelect.emit(variable);
            return;
        }

        const variableId = parseInt(variable.id, 10);

        // Add the variable's ID to the addedVariableIds array
        this.addedVariableIds.push(variableId.toString());
        const translatedVariableId = this.variableUtilityService.getIDPairByVariableId(variableId);
        if (translatedVariableId !== variableId) {
            // If the added variable has ID-NAME variable pair,
            // Emit the ID variable instead of NAME
            const idVariable = this.variableById[translatedVariableId.toString()];
            if (idVariable) {
                this.addedVariableIds.push(translatedVariableId.toString());
                idVariable.alias = variable.alias;
                idVariable.name = variable.name;
                this.variableSelect.emit(idVariable);
            }
        } else {
            this.variableSelect.emit(variable);
        }
    }

    // Optimization
    trackByProperty(index: number, propertyGroup: any): string {
        return propertyGroup.key;
    }

    trackByFormula(index: number, input: any): number | string {
        return input.id || input.name;
    }

    getFormula(variable: VariableDetails) {
        // Create a map for O(1) lookup
        const idToNameMap = new Map(
            variable.formula.inputs.map(input => [input.id.toString(), input.name])
        );

        // Replace all {{id}} patterns with corresponding names wrapped in badge
        const formulaWithBadges = variable.formula.definition.replace(/\{\{(\d+)\}\}/g, (match, id) => {
            const name = idToNameMap.get(id);
            return name ? `<span class="badge badge-pill text-bg-light border">${name}</span>` : match;
        });

        // Wrap the entire formula in Bootstrap 5.3 code styling
        return `<code class="text-body fs-6">${formulaWithBadges}</code>`;

    }
}
